// Design System Documentation
// Centralized constants and guidelines for consistent styling across the application
// CREATED: Design System implementation with primary color #3E8B82

/**
 * COLOR PALETTE GUIDELINES
 *
 * PRIMARY (Teal #3E8B82):
 * - Use for: Main CTAs, primary actions, interactive elements, links
 * - Variants: primary-50 to primary-900 (see tailwind.config.js)
 * - Examples: Submit buttons, Save actions, Create buttons, Primary navigation items
 *
 * SUCCESS (Green):
 * - Use for: Task completion, success messages, positive confirmations
 * - Examples: "Task completed" states, Success toasts, Validation checkmarks
 *
 * WARNING (Amber):
 * - Use for: Warnings, cautions, offline states
 * - Examples: Offline banner, Warning messages, Attention alerts
 *
 * ERROR/DANGER (Red):
 * - Use for: Destructive actions, errors, dangerous operations
 * - Examples: Delete buttons, Error messages, Cancel operations
 *
 * INFO (Blue):
 * - Use for: Informational messages, neutral actions
 * - Examples: Info tooltips, Help text, Non-critical notifications
 *
 * ROLE COLORS (Limited Usage):
 * - Admin (Rose), Director (Amber), Manager (Emerald), Operator (Blue)
 * - Use ONLY for: Role badges, user avatars/initials, subtle role indicators
 * - DO NOT use for: CTAs, buttons, main navigation (use primary instead)
 */

/**
 * BUTTON VARIANTS
 * Use the unified <Button> component from src/components/ui/Button.tsx
 *
 * Available variants:
 * - primary: Main actions (bg-primary-600)
 * - secondary: Less prominent actions (bg-gray-100)
 * - outline: Alternative primary actions (border-primary-300)
 * - ghost: Minimal actions (transparent background)
 * - danger: Destructive actions (bg-red-600)
 * - success: Positive confirmations (bg-green-600)
 *
 * Sizes: sm, md (default), lg
 */

/**
 * FOCUS RING STANDARDS
 * All interactive elements should use consistent focus rings for accessibility
 * Standard: focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
 */
export const FOCUS_RING_CLASSES = 'focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none';

/**
 * TRANSITION STANDARDS
 * Consistent transitions for smooth interactions
 */
export const TRANSITION_CLASSES = 'transition-all duration-200';

/**
 * BORDER RADIUS STANDARDS
 * Consistent border radius for visual harmony
 */
export const BORDER_RADIUS = {
  sm: 'rounded-md',    // 0.375rem / 6px
  md: 'rounded-lg',    // 0.5rem / 8px - DEFAULT for buttons
  lg: 'rounded-xl',    // 0.75rem / 12px
  full: 'rounded-full', // 9999px - for circles
} as const;

/**
 * SHADOW STANDARDS
 * Consistent shadows for depth and hierarchy
 */
export const SHADOWS = {
  sm: 'shadow-sm',           // Subtle shadow for cards
  md: 'shadow-md',           // Medium shadow for elevated elements
  lg: 'shadow-lg',           // Large shadow for modals
  card: 'shadow-card',       // Custom card shadow
  cardHover: 'shadow-card-hover', // Card hover state
} as const;

/**
 * SPACING STANDARDS
 * Follow the 8px grid system for consistent spacing
 * Use Tailwind spacing scale: 0, 1 (4px), 2 (8px), 3 (12px), 4 (16px), 6 (24px), 8 (32px), 12 (48px), 16 (64px)
 */

/**
 * TYPOGRAPHY STANDARDS
 * Consistent text hierarchy across the application
 * Based on Material Design 3 and Apple HIG principles
 *
 * HIERARCHY LEVELS:
 * 1. Modal/Page Title - Largest, establishes primary context
 * 2. Section Heading - Divides content into functional zones
 * 3. Field Label - Identifies individual form inputs
 * 4. Helper Text - Provides additional context or guidance
 * 5. Input Content - User-entered or displayed data
 *
 * GUIDELINES:
 * - Maintain clear visual hierarchy with size, weight, and color
 * - Use uppercase sparingly (only for section headings)
 * - Ensure sufficient contrast for readability (WCAG AA)
 * - Keep line-height appropriate for text size (120% for headings, 150% for body)
 */

export const TYPOGRAPHY = {
  // Modal/Page Titles
  modalTitle: 'text-xl font-bold text-gray-900',           // 20px, 700 weight
  pageTitle: 'text-2xl font-bold text-gray-900',           // 24px, 700 weight

  // Section Headings (use in forms to divide functional areas)
  sectionHeading: 'text-xs font-semibold uppercase tracking-wider text-gray-500', // 12px, 600 weight, uppercase

  // Field Labels
  fieldLabel: 'text-sm font-medium text-gray-700',         // 14px, 500 weight
  fieldLabelOptional: 'text-sm font-medium text-gray-500', // 14px, 500 weight, muted

  // Helper Text & Descriptions
  helperText: 'text-xs text-gray-500',                     // 12px, 400 weight
  errorText: 'text-xs text-red-600',                       // 12px, 400 weight
  successText: 'text-xs text-green-600',                   // 12px, 400 weight

  // Input Content
  inputText: 'text-sm text-gray-900',                      // 14px, 400 weight
  inputPlaceholder: 'text-sm text-gray-400 placeholder:text-gray-400', // 14px, 400 weight

  // Body Text
  bodyLarge: 'text-base text-gray-900',                    // 16px, 400 weight
  bodyRegular: 'text-sm text-gray-900',                    // 14px, 400 weight
  bodySmall: 'text-xs text-gray-600',                      // 12px, 400 weight

  // Links
  link: 'text-sm text-primary-600 hover:text-primary-700 underline', // 14px
  linkSmall: 'text-xs text-primary-600 hover:text-primary-700 underline', // 12px
} as const;

/**
 * COLOR USAGE EXAMPLES
 */
export const COLOR_USAGE = {
  // Buttons
  primaryButton: 'bg-primary-600 hover:bg-primary-700 text-white',
  secondaryButton: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200',
  dangerButton: 'bg-red-600 hover:bg-red-700 text-white',
  successButton: 'bg-green-600 hover:bg-green-700 text-white',

  // Text
  primaryText: 'text-gray-900',
  secondaryText: 'text-gray-600',
  mutedText: 'text-gray-500',
  linkText: 'text-primary-600 hover:text-primary-700',

  // Backgrounds
  primaryBg: 'bg-white',
  secondaryBg: 'bg-gray-50',
  hoverBg: 'hover:bg-gray-100',

  // Borders
  defaultBorder: 'border border-gray-200',
  focusBorder: 'border-primary-300',
  errorBorder: 'border-red-300',
} as const;

/**
 * ACCESSIBILITY GUIDELINES
 *
 * 1. All interactive elements must have visible focus indicators
 * 2. Color contrast must meet WCAG AA standards (4.5:1 for normal text)
 * 3. Interactive elements must be at least 44x44px (mobile)
 * 4. Use semantic HTML elements (button, not div with onClick)
 * 5. Provide alternative text for images and icons
 */

/**
 * COMPONENT CHECKLIST
 * When creating or updating components:
 *
 * ✓ Use primary-600 for main CTAs
 * ✓ Use standardized focus rings (FOCUS_RING_CLASSES)
 * ✓ Use consistent transitions (TRANSITION_CLASSES)
 * ✓ Use rounded-lg for buttons
 * ✓ Implement all interactive states (hover, focus, active, disabled)
 * ✓ Ensure WCAG AA contrast ratios
 * ✓ Test keyboard navigation
 * ✓ Test mobile responsiveness
 */
